import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Send, Trash2, Edit3, Check, X, Loader2,
  Paperclip, Bot, User, Copy, ChevronDown,
  MessageSquare, FileText, Image as ImageIcon, Search,
  Images, Download, Eye, ArrowLeft
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const MODEL_OPTIONS = [
  { key: "gpt-5.2", label: "GPT-5.2", icon: "G", color: "bg-emerald-500", type: "text" },
  { key: "claude-sonnet", label: "Claude 4.5", icon: "C", color: "bg-orange-500", type: "text" },
  { key: "nano-banana", label: "Nano Banana", icon: "N", color: "bg-yellow-500", type: "image" },
];

function CodeBlock({ children, className }) {
  const match = /language-(\w+)/.exec(className || "");
  const lang = match ? match[1] : "";
  const code = String(children).replace(/\n$/, "");

  return (
    <div className="relative group my-2">
      <div className="flex items-center justify-between bg-zinc-800 text-zinc-400 text-xs px-3 py-1.5 rounded-t-md">
        <span>{lang || "code"}</span>
        <button
          onClick={() => { navigator.clipboard.writeText(code); toast.success("Kopiert!"); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-white"
          data-testid="copy-code-btn"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <pre className="bg-zinc-900 text-zinc-100 p-3 rounded-b-md overflow-x-auto text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function ChatMessage({ message }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex gap-3 px-4 py-4 ${isUser ? "" : "bg-slate-50"}`} data-testid={`chat-message-${message.role}`}>
      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${isUser ? "bg-slate-700" : "bg-violet-600"}`}>
        {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <p className="text-xs font-semibold text-slate-500">{isUser ? "Du" : "Assistent"}</p>

        {message.file_name && (
          <div className="inline-flex items-center gap-2 bg-white border rounded-md px-2.5 py-1.5 text-xs">
            {message.file_type?.startsWith("image/") ? (
              <ImageIcon className="w-3.5 h-3.5 text-blue-500" />
            ) : (
              <FileText className="w-3.5 h-3.5 text-amber-500" />
            )}
            <span className="text-slate-700">{message.file_name}</span>
          </div>
        )}

        {message.file_url && message.file_type?.startsWith("image/") && (
          <img src={message.file_url} alt="" className="max-w-xs rounded-md border" />
        )}

        {/* AI-generated image */}
        {message.image_url && (
          <div className="my-2">
            <img
              src={message.image_url}
              alt="KI-generiertes Bild"
              className="max-w-md rounded-lg border shadow-sm"
              data-testid="generated-image"
            />
            <a
              href={message.image_url}
              download="generated_image.png"
              className="inline-flex items-center gap-1 mt-1.5 text-xs text-violet-600 hover:text-violet-700 hover:underline"
              data-testid="download-image-btn"
            >
              <ChevronDown className="w-3 h-3" /> Bild herunterladen
            </a>
          </div>
        )}

        <div className="prose prose-sm prose-slate max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:leading-relaxed [&_li]:leading-relaxed">
          <ReactMarkdown
            components={{
              code({ inline, className, children, ...props }) {
                if (inline) {
                  return <code className="bg-slate-200 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>;
                }
                return <CodeBlock className={className}>{children}</CodeBlock>;
              },
              a({ href, children }) {
                return <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">{children}</a>;
              }
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>
      </div>

      {!isUser && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => { navigator.clipboard.writeText(message.content); toast.success("Kopiert!"); }}
                className="opacity-0 group-hover:opacity-100 self-start mt-1 p-1 rounded hover:bg-slate-200 transition-all"
                data-testid="copy-message-btn"
              >
                <Copy className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Kopieren</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/* =================== GALLERY LIGHTBOX =================== */
function ImageLightbox({ image, onClose }) {
  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <div
        className="relative max-w-4xl w-full bg-zinc-900 rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="flex-1 min-w-0 mr-4">
            <p className="text-sm text-zinc-200 truncate">{image.prompt || "Generiertes Bild"}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              {image.conversation_title} &middot; {image.created_at ? new Date(image.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { navigator.clipboard.writeText(image.prompt || ""); toast.success("Prompt kopiert!"); }}
                    className="p-1.5 rounded-md hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                    data-testid="lightbox-copy-prompt"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Prompt kopieren</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <a
              href={image.image_url}
              download={`bild_${image.id || "gen"}.png`}
              className="p-1.5 rounded-md hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
              data-testid="lightbox-download"
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Image */}
        <div className="flex items-center justify-center bg-zinc-950 min-h-[300px] max-h-[70vh]">
          <img
            src={image.image_url}
            alt={image.prompt || "Bild"}
            className="max-w-full max-h-[70vh] object-contain"
          />
        </div>
        {/* Prompt bar */}
        {image.prompt && (
          <div className="px-4 py-3 border-t border-zinc-800">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Prompt</p>
            <p className="text-xs text-zinc-300 leading-relaxed">{image.prompt}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* =================== GALLERY VIEW =================== */
function GalleryView({ onOpenImage, onGoToConversation }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/chat/gallery`, { headers });
        setImages(res.data.images || []);
      } catch {
        toast.error("Fehler beim Laden der Galerie");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <div className="w-14 h-14 bg-yellow-50 rounded-2xl flex items-center justify-center mb-4">
          <Images className="w-7 h-7 text-yellow-500" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800 mb-1" data-testid="gallery-empty">Keine Bilder</h2>
        <p className="text-sm text-slate-500 max-w-sm">
          Verwende das Modell <span className="font-medium text-yellow-600">Nano Banana</span> um Bilder zu generieren. Alle generierten Bilder erscheinen hier.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-y-auto h-full" data-testid="gallery-view">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
          <Images className="w-4 h-4 text-yellow-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Bildergalerie</h2>
          <p className="text-xs text-slate-500">{images.length} {images.length === 1 ? "Bild" : "Bilder"} generiert</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img) => (
          <div
            key={img.id}
            className="group relative bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer"
            data-testid={`gallery-item-${img.id}`}
          >
            {/* Thumbnail */}
            <div className="aspect-square bg-slate-100 relative overflow-hidden" onClick={() => onOpenImage(img)}>
              <img
                src={img.image_url}
                alt={img.prompt || "Bild"}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>

            {/* Info */}
            <div className="p-2.5">
              <p className="text-xs text-slate-700 line-clamp-2 leading-relaxed mb-1.5">{img.prompt || "Generiertes Bild"}</p>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-400">
                  {img.created_at ? new Date(img.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short" }) : ""}
                </span>
                <div className="flex gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(img.prompt || ""); toast.success("Prompt kopiert!"); }}
                          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          data-testid="gallery-copy-prompt"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Prompt kopieren</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <a
                    href={img.image_url}
                    download={`bild_${img.id || "gen"}.png`}
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="gallery-download"
                  >
                    <Download className="w-3 h-3" />
                  </a>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => { e.stopPropagation(); onGoToConversation(img.conversation_id); }}
                          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          data-testid="gallery-go-to-chat"
                        >
                          <MessageSquare className="w-3 h-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Zum Gespräch</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =================== MAIN PAGE =================== */
export default function AiChatPage() {
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedModel, setSelectedModel] = useState("gpt-5.2");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("chats"); // "chats" | "gallery"
  const [lightboxImage, setLightboxImage] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const loadConversations = useCallback(async () => {
    try {
      setLoadingConvs(true);
      const res = await axios.get(`${API}/api/chat/conversations`, { headers });
      setConversations(res.data);
    } catch { /* ignore */ } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      setLoadingMessages(true);
      const res = await axios.get(`${API}/api/chat/conversations/${convId}`, { headers });
      setActiveConv(res.data.conversation);
      setMessages(res.data.messages);
      setSelectedModel(res.data.conversation.model || "gpt-5.2");
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (activeConvId) loadMessages(activeConvId);
    else { setMessages([]); setActiveConv(null); }
  }, [activeConvId, loadMessages]);

  const handleNewChat = async () => {
    try {
      const res = await axios.post(`${API}/api/chat/conversations`, { model: selectedModel }, { headers });
      setConversations(prev => [res.data, ...prev]);
      setActiveConvId(res.data.id);
      setSidebarTab("chats");
      setInput("");
      setPendingFile(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleDeleteConv = async (convId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/chat/conversations/${convId}`, { headers });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConvId === convId) { setActiveConvId(null); setMessages([]); }
      toast.success("Gelöscht");
    } catch {
      toast.error("Fehler");
    }
  };

  const startEditTitle = (conv, e) => {
    e.stopPropagation();
    setEditingTitle(conv.id);
    setEditTitleValue(conv.title);
  };

  const saveTitle = async (convId) => {
    try {
      await axios.put(`${API}/api/chat/conversations/${convId}`, { title: editTitleValue }, { headers });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, title: editTitleValue } : c));
      if (activeConv?.id === convId) setActiveConv(prev => ({ ...prev, title: editTitleValue }));
    } catch { /* ignore */ }
    setEditingTitle(null);
  };

  const handleModelChange = async (model) => {
    setSelectedModel(model);
    if (activeConvId) {
      try {
        await axios.put(`${API}/api/chat/conversations/${activeConvId}`, { model }, { headers });
        setActiveConv(prev => prev ? { ...prev, model } : prev);
      } catch { /* ignore */ }
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && !pendingFile) || sending) return;

    const messageText = input.trim();
    setInput("");
    setSending(true);

    let convId = activeConvId;

    if (!convId) {
      try {
        const res = await axios.post(`${API}/api/chat/conversations`, { model: selectedModel }, { headers });
        convId = res.data.id;
        setConversations(prev => [res.data, ...prev]);
        setActiveConvId(convId);
        setActiveConv(res.data);
        setSidebarTab("chats");
      } catch {
        toast.error("Fehler");
        setSending(false);
        return;
      }
    }

    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: messageText || (pendingFile ? `Datei: ${pendingFile.name}` : ""),
      file_name: pendingFile?.name,
      file_type: pendingFile?.type,
      file_url: pendingFile?.preview,
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      let response;

      if (pendingFile) {
        const formData = new FormData();
        formData.append("file", pendingFile.file);
        formData.append("content", messageText);
        response = await axios.post(
          `${API}/api/chat/conversations/${convId}/messages/with-file`,
          formData,
          { headers: { ...headers, "Content-Type": "multipart/form-data" } }
        );
        setPendingFile(null);
      } else {
        response = await axios.post(
          `${API}/api/chat/conversations/${convId}/messages`,
          { content: messageText },
          { headers }
        );
      }

      const { user_message, assistant_message } = response.data;

      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        return [...filtered, user_message, assistant_message];
      });

      setTimeout(() => loadConversations(), 2000);

    } catch (err) {
      toast.error("KI-Antwort fehlgeschlagen");
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }

    let preview = null;
    if (file.type.startsWith("image/")) {
      preview = URL.createObjectURL(file);
    }
    setPendingFile({ file, name: file.name, type: file.type, preview });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGoToConversation = (convId) => {
    setSidebarTab("chats");
    setActiveConvId(convId);
  };

  const filteredConvs = conversations.filter(c =>
    c.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentModelOption = MODEL_OPTIONS.find(m => m.key === selectedModel) || MODEL_OPTIONS[0];
  const showGalleryMain = sidebarTab === "gallery" && !activeConvId;

  return (
    <Layout>
      <div className="flex h-full overflow-hidden" data-testid="ai-chat-page">
        {/* Sidebar */}
        <div className="w-64 bg-zinc-900 flex flex-col flex-shrink-0" data-testid="chat-sidebar">
          {/* Tab Toggle */}
          <div className="flex border-b border-zinc-800">
            <button
              onClick={() => setSidebarTab("chats")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                sidebarTab === "chats"
                  ? "text-white border-b-2 border-violet-500"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid="tab-chats"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {t("chats")}
            </button>
            <button
              onClick={() => { setSidebarTab("gallery"); setActiveConvId(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                sidebarTab === "gallery"
                  ? "text-white border-b-2 border-yellow-500"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid="tab-gallery"
            >
              <Images className="w-3.5 h-3.5" />
              {t("gallery")}
            </button>
          </div>

          {/* New Chat */}
          <div className="p-3">
            <Button
              onClick={handleNewChat}
              variant="outline"
              className="w-full justify-start bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white"
              data-testid="new-chat-btn"
            >
              <Plus className="w-4 h-4 mr-2" />{t("new_conversation")}
            </Button>
          </div>

          {/* Search */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suchen..."
                className="pl-8 h-8 bg-zinc-800 border-zinc-700 text-zinc-300 placeholder:text-zinc-600 text-xs"
                data-testid="search-conversations"
              />
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto px-2 space-y-0.5">
            {loadingConvs ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
              </div>
            ) : filteredConvs.length === 0 ? (
              <p className="text-xs text-zinc-600 text-center py-6">{t("no_conversations")}</p>
            ) : (
              filteredConvs.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => { setActiveConvId(conv.id); setSidebarTab("chats"); }}
                  className={`group flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer transition-colors ${
                    activeConvId === conv.id ? "bg-zinc-700" : "hover:bg-zinc-800"
                  }`}
                  data-testid={`conv-item-${conv.id}`}
                >
                  {conv.model === "nano-banana" ? (
                    <ImageIcon className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
                  ) : (
                    <MessageSquare className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                  )}

                  {editingTitle === conv.id ? (
                    <div className="flex-1 flex items-center gap-1">
                      <input
                        value={editTitleValue}
                        onChange={(e) => setEditTitleValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveTitle(conv.id)}
                        className="flex-1 bg-zinc-600 text-white text-xs px-1 py-0.5 rounded outline-none"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button onClick={(e) => { e.stopPropagation(); saveTitle(conv.id); }} className="text-green-400 hover:text-green-300">
                        <Check className="w-3 h-3" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setEditingTitle(null); }} className="text-zinc-400 hover:text-zinc-300">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-300 truncate">{conv.title}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${conv.model === "claude-sonnet" ? "bg-orange-400" : conv.model === "nano-banana" ? "bg-yellow-400" : "bg-emerald-400"}`} />
                          <span className="text-[10px] text-zinc-600">{conv.model === "claude-sonnet" ? "Claude" : conv.model === "nano-banana" ? "Nano" : "GPT"}</span>
                        </div>
                      </div>
                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => startEditTitle(conv, e)} className="p-0.5 rounded hover:bg-zinc-600">
                          <Edit3 className="w-3 h-3 text-zinc-400" />
                        </button>
                        <button onClick={(e) => handleDeleteConv(conv.id, e)} className="p-0.5 rounded hover:bg-zinc-600">
                          <Trash2 className="w-3 h-3 text-zinc-400" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Model info */}
          <div className="p-3 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-[10px] text-zinc-500">
              <div className={`w-2 h-2 rounded-full ${currentModelOption.color}`} />
              <span>{currentModelOption.label}</span>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col bg-white">
          {showGalleryMain ? (
            /* Gallery View */
            <GalleryView
              onOpenImage={setLightboxImage}
              onGoToConversation={handleGoToConversation}
            />
          ) : (
            <>
              {/* Top Bar */}
              <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
                <div className="flex items-center gap-2">
                  {activeConv && <p className="text-sm font-medium text-slate-700 truncate max-w-[300px]">{activeConv.title}</p>}
                </div>
                <Select value={selectedModel} onValueChange={handleModelChange}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="model-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.map(m => (
                      <SelectItem key={m.key} value={m.key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded text-white text-[10px] font-bold flex items-center justify-center ${m.color}`}>
                            {m.icon}
                          </div>
                          {m.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto" data-testid="messages-area">
                {messages.length === 0 && !loadingMessages ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                      <Bot className="w-7 h-7 text-violet-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-800 mb-1">KI-Chat</h2>
                    <p className="text-sm text-slate-500 max-w-md mb-6">
                      Fragen beantworten, Code schreiben, Dokumente analysieren, Bilder generieren.
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-w-md">
                      {[
                        "Erkläre mir EMS-Training",
                        "Schreibe einen Social Media Post",
                        "Erstelle einen Trainingsplan",
                        "Generiere ein Bild von einem EMS-Studio",
                      ].map((q) => (
                        <button
                          key={q}
                          onClick={() => { setInput(q); inputRef.current?.focus(); }}
                          className="text-left text-xs bg-slate-50 hover:bg-slate-100 border rounded-lg p-2.5 text-slate-600 transition-colors"
                          data-testid="suggestion-btn"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : loadingMessages ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div>
                    {messages.map((msg) => (
                      <ChatMessage key={msg.id} message={msg} />
                    ))}
                    {sending && (
                      <div className="flex gap-3 px-4 py-4 bg-slate-50">
                        <div className="w-7 h-7 rounded-md bg-violet-600 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                          <span className="text-xs text-slate-500">{t("thinking")}</span>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Pending File Preview */}
              {pendingFile && (
                <div className="px-4 pt-2">
                  <div className="inline-flex items-center gap-2 bg-slate-100 border rounded-md px-3 py-1.5 text-xs">
                    {pendingFile.type?.startsWith("image/") ? (
                      <ImageIcon className="w-4 h-4 text-blue-500" />
                    ) : (
                      <FileText className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="text-slate-700">{pendingFile.name}</span>
                    <button onClick={() => setPendingFile(null)} className="text-slate-400 hover:text-red-500">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t bg-white">
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-end gap-2 bg-slate-50 border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-violet-200 focus-within:border-violet-300 transition-all">
                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                            data-testid="attach-file-btn"
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Datei anhängen</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={t("enter_message")}
                      rows={1}
                      className="flex-1 bg-transparent border-none outline-none resize-none text-sm text-slate-800 placeholder:text-slate-400 max-h-32 py-1"
                      style={{ minHeight: "24px", height: "auto" }}
                      onInput={(e) => {
                        e.target.style.height = "24px";
                        e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
                      }}
                      data-testid="chat-input"
                    />

                    <button
                      onClick={handleSend}
                      disabled={sending || (!input.trim() && !pendingFile)}
                      className={`p-1.5 rounded-md transition-colors ${
                        sending || (!input.trim() && !pendingFile)
                          ? "text-slate-300 cursor-not-allowed"
                          : "bg-violet-600 text-white hover:bg-violet-700"
                      }`}
                      data-testid="send-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-400 text-center mt-1.5">
                    {currentModelOption.label} kann Fehler machen. Überprüfe wichtige Informationen.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
    </Layout>
  );
}
